import React, { useEffect, useState } from "react";
import { Typography, List, ListItem, ListItemText, Divider, Button, Box, CircularProgress, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogContentText, ListItemIcon, DialogActions } from "@mui/material";
import { useCart } from "../../hooks/contexts/cartContext";
import { useAuthRouting, useSignedInUserState } from "../../hooks/useAuthState";
import Lottie from "lottie-react";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useNavigate } from "react-router-dom";
import { DateRangePill } from "../../shared/dateRangePill";
import { logEvent } from "../../firebase";
import { MetricsNames } from "../../services/firebase/metricsService";
import { useTemporaryMenuCartProviderCart } from "../../hooks/contexts/TemporaryMenuCartContext";
import { getPriceOfMenuCart, MenuCartItem } from "../../models/fullMenuCart";
import { FullMenuPageData } from "../../models/fullMenuPageData";
import { menuService } from "../../services/firebase/menuService";
import { formatMoney } from "../../services/strings/formatters";
import { RemoveFromCartButton } from "../../listingDetailsPage/removeFromCartButton";
import { MenuItemDetails } from "../../shared/menuItemDetails";
import { SOLD_OUT_ITEMS_FOUND } from "../../models/errors";
import { ShowAsSoldOut } from "../../shared/showAsSoldOut";


const OrderConfirmPage = () => {

    useAuthRouting()
    const [soldOutItems, setSoldOutItems] = useState<MenuCartItem[]>([]);
    const [cartItemLabels, setCartItemLabels] = useState<{ [cartItemId: string]: string }>({})
    const { cart, checkout, fetchCart, loading } = useCart();
    const { profile } = useSignedInUserState();
    const { transformTemporaryMenuCartToFullCart, fetchTemporaryMenuCart, temporaryMenuCart, removeTemporaryMenuCart } = useTemporaryMenuCartProviderCart()
    const [animationData, setAnimationData] = useState<any>(null);
    const animationPath = "/animation/empty_cart.json";
    const [open, setOpen] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [menuData, setMenuData] = useState<FullMenuPageData>({ baseItems: [], menu: { categories: [], bundles: [] } });
    const [locallyLoading, setLocallyLoading] = useState<boolean>(false);

    useEffect(() => {
        const getStrings = async () => {
            if (cart) {
                let newCartItemLabels: { [cartItemId: string]: string } = {}
                const promises = cart.items.map(async (element) => {
                    const itemName = element.menuCartItem.name
                    newCartItemLabels[element.menuCartItem.id] = `${itemName}`
                });
                await Promise.all(promises)
                setCartItemLabels(newCartItemLabels)
                logEvent(MetricsNames.BEGIN_CHECKOUT);
            }
        }
        // Cleanup subscription on unmount
        getStrings();
    }, []);

    const fetchMenu = async (restaurantId?: string) => {
        if (restaurantId) {
            try {
                const menu = await menuService.fetchFullMenuCached(restaurantId);
                console.log("full menu", menu)
                setMenuData(menu);
            } catch (err) {
                console.log("there was an error fetching the full menu", err)
                // setError((err as Error).message);
            }
        }
    };

    useEffect(() => {
        transformMenuCart();
    }, [profile])

    const transformMenuCart = async () => {
        setLocallyLoading(true);
        console.log("fetching the temporary menu cart")
        const tmpcart = await fetchTemporaryMenuCart(); // this is the same as temporaryMenuCart
        console.log("found cart", tmpcart)
        console.log("transforming the cart")
        const response = await transformTemporaryMenuCartToFullCart();
        if (!!response && response.soldOutItems && response.error == SOLD_OUT_ITEMS_FOUND().message) {
            setSoldOutItems(response.soldOutItems)
        }
        const tmpCart = await fetchCart(); // this is the same as cart
        if (tmpCart) {
            await fetchMenu(tmpCart.restaurantId);
        } else {
            await fetchMenu(tmpcart?.restaurantId);
        }
        setLocallyLoading(false);
    }

    useEffect(() => {
        fetch(animationPath)
            .then((response) => response.json())
            .then((data) => setAnimationData(data))
            .catch((error) => console.error("Error loading Lottie animation:", error));
    }, [animationPath]);

    const handleCheckout = async () => {
        setCheckoutLoading(true);
        if (!loading) {
            const success = await checkout();
            if (!success) {
                await transformMenuCart()
            }
        } else {
            return;
        }
    };

    const navigate = useNavigate();

    if (loading || locallyLoading) {
        return <Box
            sx={{
                backgroundColor: "#f5f5f5",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 4,
            }}
            className={"patlite"}

        >
            <CircularProgress size={60} />
        </Box>;
    }

    console.log("!cart && temporaryMenuCart && soldOutItems.length > 0", cart, temporaryMenuCart, soldOutItems.length)
    if (temporaryMenuCart && soldOutItems.length > 0) {
        return <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 2,
            }}
            className={"patlite"}
        >
            <Card sx={{
                padding: 4,
                justifyContent: "center",
                display: "flex",
                flexDirection: "column",
            }}>
                <Typography textAlign={"center"} variant="h4" gutterBottom>
                    Confirm Your Order
                </Typography>
                <List>
                    {temporaryMenuCart.items.length > 0 && temporaryMenuCart.items.map((item) => (
                        <React.Fragment key={item.id}>
                            <ListItem alignItems="flex-start" disableGutters>
                                <Box
                                    display="flex"
                                    flexDirection={{ xs: 'column', sm: 'row' }}
                                    justifyContent="space-between"
                                    width="100%"
                                    gap={2}
                                >
                                    <ShowAsSoldOut
                                        isSoldOut={soldOutItems.some(soi => soi.id === item.id)}>
                                        <MenuItemDetails item={item} menuData={menuData} />
                                    </ShowAsSoldOut>
                                    <Box
                                        display="flex"
                                        justifyContent={{ xs: 'flex-start', sm: 'center' }}
                                        mt={{ xs: 2, sm: 0 }}
                                    >
                                        <RemoveFromCartButton
                                            menuCartItem={item}
                                            onError={async (err) => console.error(err)}
                                            onSuccess={transformMenuCart} />

                                    </Box>
                                </Box>
                            </ListItem>
                            <Divider />
                        </React.Fragment>
                    ))}
                </List>
                <ListItem
                    component="button"
                    onClick={() => setOpen(true)}
                    sx={{
                        justifyContent: "space-between",
                        display: "start",
                        border: "1px solid transparent",
                        py: 2,
                        px: 0,
                        width: "100%",
                        backgroundColor: "transparent",
                        "&:hover": {
                            backgroundColor: "transparent",
                        },
                    }}
                >
                    <ListItemText
                        primary="Allergies and Dietary Requirements"
                        primaryTypographyProps={{ sx: { fontWeight: 500, textAlign: "start", justifyContent: "start", alignContent: "start" } }}
                    />
                    <ListItemIcon sx={{ minWidth: "auto" }}>
                        <KeyboardArrowRightIcon />
                    </ListItemIcon>
                </ListItem>

                <Button
                    variant="outlined"
                    onClick={async () => { await removeTemporaryMenuCart(); navigate(-1) }}
                    color="success"
                    sx={{ margin: "16px 0px", border: "1px solid #162C1F", borderRadius: "24px" }}
                >
                    <Typography variant="button" component="h1" sx={{ color: "#000" }}>
                        Cancel
                    </Typography>
                </Button>
            </Card>
        </Box>
    }

    else if (!cart) {
        return <Box
            sx={{
                backgroundColor: "tranparent",
                minHeight: "100vh",
                display: "flex",
                alignItems: "start",
                justifyContent: "center",
                padding: 4,
            }}
            className={"patlite"}

        >
            <Card
                sx={{
                    margin: "5rem auto",
                    height: "50vh",
                    borderRadius: 2,
                    boxShadow: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    maxWidth: "400px",
                }}
            >
                <CardContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                    }}
                >
                    <Lottie animationData={animationData} allowTransparency={true} />
                    <Typography
                        variant="h3"
                        sx={{
                            color: "#6c757d",
                            fontSize: {
                                xs: "1.5rem",  // Mobile view
                                sm: "2rem",    // Tablet view
                                md: "2.5rem",  // Desktop view
                            },
                            marginBottom: 2,
                        }}
                    >
                        Your Cart Is Empty
                    </Typography>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            color: "#6c757d",
                        }}
                    >
                        Please add eco-friendly items to your cart before proceeding to checkout. Every thoughtful choice makes a positive impact on our planet.                </Typography>
                </CardContent>
            </Card>
        </Box>;

    }

    return <Box
        sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 2,
        }}
        className={"patlite"}
    >
        <Card sx={{
            padding: 4,
            justifyContent: "center",
            display: "flex",
            flexDirection: "column",
        }}>
            <Typography textAlign={"center"} variant="h4" gutterBottom>
                Confirm Your Order
            </Typography>
            <List>
                {cart && cart.items.length > 0 && cart.items.map((item) => (
                    <React.Fragment key={item.menuCartItem.id}>
                        <ListItem alignItems="flex-start" disableGutters>
                            <Box
                                display="flex"
                                flexDirection={{ xs: 'column', sm: 'row' }}
                                justifyContent="space-between"
                                width="100%"
                                gap={2}
                            >
                                <MenuItemDetails item={item.menuCartItem} menuData={menuData} />
                                <Box
                                    display="flex"
                                    justifyContent={{ xs: 'flex-start', sm: 'center' }}
                                    mt={{ xs: 2, sm: 0 }}
                                >
                                    <RemoveFromCartButton
                                        menuCartItem={item.menuCartItem}
                                        onError={async (err) => console.error(err)}
                                        onSuccess={transformMenuCart} />

                                </Box>
                            </Box>
                        </ListItem>
                        <Divider />
                    </React.Fragment>
                ))}
            </List>
            <Typography variant="h5" style={{ marginTop: "1rem" }}>
                Total: R{Math.round(cart.totalAmount) / 100}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Total Before Fees: R{Math.round(cart.totalAmount - cart.totalFees) / 100}
            </Typography>
            <Typography variant="body2" color="text.secondary" style={{ marginTop: "1rem" }}>
                Fees: R{Math.round(cart.totalFees) / 100}
            </Typography>
            <ListItem
                component="button"
                onClick={() => setOpen(true)}
                sx={{
                    justifyContent: "space-between",
                    display: "start",
                    border: "1px solid transparent",
                    py: 2,
                    px: 0,
                    width: "100%",
                    backgroundColor: "transparent",
                    "&:hover": {
                        backgroundColor: "transparent",
                    },
                }}
            >
                <ListItemText
                    primary="Allergies and Dietary Requirements"
                    primaryTypographyProps={{ sx: { fontWeight: 500, textAlign: "start", justifyContent: "start", alignContent: "start" } }}
                />
                <ListItemIcon sx={{ minWidth: "auto" }}>
                    <KeyboardArrowRightIcon />
                </ListItemIcon>
            </ListItem>

            {/* Modal Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Allergies and Dietary Requirements</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ whiteSpace: "pre-line" }}>
                        {"Allergies and dietary requirements must be confirmed directly with the store. Eco Eats cannot guarantee the contents or suitability of Surprise Bags for any allergies, intolerances, or dietary needs.\n\nPlease ensure to check the items upon arrival and collection."}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button sx={{ color: "#fff", backgroundColor: "#162C1F", "&:hover": { backgroundColor: "#0f1f15" }, borderRadius: "24px" }}
                        onClick={() => setOpen(false)} autoFocus>
                        Okay
                    </Button>
                </DialogActions>
            </Dialog>
            <Button
                onClick={handleCheckout}
                variant="contained"
                color="success"
                disabled={loading}
                sx={{
                    marginTop: "1rem",
                    background: "#162C1F",
                    border: "1px solid #162C1F",
                    borderRadius: "24px",
                    minWidth: "180px",
                    height: "40px",
                }}
            >
                {checkoutLoading ? (
                    <CircularProgress size={24} sx={{ color: "#FFF" }} />
                ) : (
                    "Confirm Purchase"
                )}
            </Button>
            <Button
                variant="outlined"
                onClick={async () => { await removeTemporaryMenuCart(); navigate(-1) }}
                color="success"
                sx={{ margin: "16px 0px", border: "1px solid #162C1F", borderRadius: "24px" }}
            >
                <Typography variant="button" component="h1" sx={{ color: "#000" }}>
                    Cancel
                </Typography>
            </Button>
        </Card>
    </Box>


};

export default OrderConfirmPage;
